import React, {useCallback, useEffect, useState} from 'react';
import { browserHistory } from 'react-router';
import PropTypes from "prop-types";
import Select from 'react-select';
import LoadingIndicatorHeader from '../loading-indicator/loading-indicator-header';
import RuleFormButtons from './rule-form-buttons';
import RuleFormTimeSlots from './rule-form-timeslots';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import ruleFormValidation from './rule-form-validation';
import Tooltip from "../tooltip/tooltip";
import ajax from '../util/util-ajax';
import util from '../util/util';
import _ from "lodash";
import { v4 } from 'uuid';

const jQuery = window.jQuery;

export default function RuleForm (props) {
  const [id, setId] = useState(0); // id of the rule we are editing (0 means new rule to create)
  const [name, setName] = useState(``); // the Rule name
  const [sourceType, setSourceType] = useState(`pod`); // the Rule Source type
  const [pod, setPod] = useState(null); // the corresponding Pod, if the Rule is made from a Pod
  const [campaign, setCampaign] = useState(null); // the corresponding Campaign, if the Rule is made from a Campaign
  const [frequency, setFrequency] = useState(3); // the Rule repeats: Daily(3), Weekly(2) or Monthly(1)
  const [startDate, setStartDate] = useState(moment(new Date())); // Rule start date
  const [endDate, setEndDate] = useState(moment(new Date())); // Rule end date
  const [ruleInterval, setRuleInterval] = useState(1); // Rule interval
  const [ruleByweekday, setRuleByweekday] = useState([]); // Rule week interval
  const [ruleBymonthday, setRuleBymonthday] = useState(1); // Rule month interval
  const [airtimeType, setAirtimeType] = useState(`timeSlots`); // The type of Ad airtime, time based or show based
  const [timeslots, setTimeslots] = useState([]); // the selected Timeslots (every 30 mins, from 00:28 up to 23:58)
  const [shows, setShows] = useState([]); // array used to control the SELECT fields for Shows
  const [loaded, setLoaded] = useState(false); // indicator for when the form has loaded
  const [errors, setErrors] = useState({}); // prop names determine which errors to show
  const [podOptions, setPodOptions] = useState([]); // the Options for the Pod SELECT
  const [campaignOptions, setCampaignOptions] = useState([]); // the Options for the Campaign SELECT
  const [showOptions, setShowOptions] = useState([]); // the Options for the Show SELECT
  const [searchingForPods, setSearchingForPods] = useState(false); // used to control the message displayed on the SELECT while searching for Pods
  const [searchingForCampaigns, setSearchingForCampaigns] = useState(false); // used to control the message displayed on the SELECT while searching for Campaigns
  const [searchingForShows, setSearchingForShows] = useState(false); // used to control the message displayed on the SELECT while searching for Shows
  const [positionBeforeShow, setPositionBeforeShow] = useState(false);  //if the Ad is set to play before the selected Show(s)
  const [positionAfterShow, setPositionAfterShow] = useState(false);  //if the Ad is set to play after the selected Show(s)


  useEffect(() => {
    // id of the rule we are editing (0 means new rule to create)
    const id = props.params ? Number(props.params.id) : 0;

    // get the rule data to display in the form;
    const getRule = () => {
      // on a new Rule, no need to set up all the values as the default ones are used
      if (id === 0) {
        // if a pod was pre-selected, go get the data and set it as the value of the SELECT
        if (props.params && props.params.podId) {
          util.getPod(props.params.podId)
            .then((data) => {
              setPod({
                label: data.name,
                value: data.id,
                id: data.id.toString()
              });
              setLoaded(true);
            });
        // if a campaign was pre-selected, go get the data amd set it as the value of the SELECT
        } else if (props.params && props.params.campaignId) {
          util.getCampaign(props.params.campaignId)
            .then((data) => {
              setCampaign({
                label: data.name,
                value: data.id,
                id: data.id.toString()
              });
              setSourceType(`campaign`);
              setLoaded(true);
            });
        // otherwise we're done, use the empty value and show the form
        } else {
          setPod(getDefaultPodValue());
          setShows(getDefaultShowsValue());
          setLoaded(true);
        }

      // on an existing Rule
      } else {
        util.getRule(id)
          .then((data) => {
            setTimeout(() => {
              setId(id);
              setName(data.name);
              if (data.pod) {
                setPod({
                  label: data.pod.name,
                  value: data.pod.id,
                  id: data.pod.id.toString()
                });
              } else {
                setCampaign({
                  label: data.campaign.name,
                  value: data.campaign.id,
                  id: data.campaign.id.toString()
                });
                setSourceType(`campaign`);
              }
              setFrequency(data.frequency);
              setStartDate(moment(data.start_date));
              setEndDate(moment(data.end_date));
              data.rule.interval && setRuleInterval(data.rule.interval); // rule values may not be defined even for existing rules
              data.rule.byweekday && setRuleByweekday(data.rule.byweekday); // same as above
              data.rule.bymonthday && setRuleBymonthday(data.rule.bymonthday); // same as above
              if (data.timeslots.length > 0) {
                setTimeslots(util.returnCleanTimeSlots(data.timeslots));
              } else {
                setShows(data.shows);
                setAirtimeType(`showBased`);
                setPositionBeforeShow(data.position_before_show);
                setPositionAfterShow(data.position_after_show);
              }
              setLoaded(true);
            });
          }, util.delayDetail);
      }
    }

    getRule();
    setLoaded(true);
  }, [props.params]);

  // default value of the Pod SELECT
  const getDefaultPodValue = () => {
    return {
      label: `Select a pod`,
      value: 0,
      id: v4()
    };
  }

  // default value of the Campaign SELECT
  const getDefaultCampaignValue = () => {
    return {
      label: `Select a campaign`,
      value: 0,
      id: v4()
    };
  }

  // default value of the SELECT;
  const getDefaultShowsValue = () => {
    return [{
      label: `Select a show`,
      value: 0,
      id: v4()
    }]
  };

  // add another form field to the page so the user can select another show;
  const addShow = () => {
    const showsArray = [...shows];
    showsArray.push(getDefaultShowsValue()[0]);
    setShows(showsArray);
  }

  const getShowsInUse = () => {
    const showIds = [];

    shows && shows.forEach((show) => {
      // selecting only the not empty ('Select a show') shows
      show.value > 0 && showIds.push(show.value);
    });

    return showIds;
  }

  const getSelectSearchStatus = (searchTerm, sourceType) => {
    if (searchTerm.inputValue.length === 0) {
      return `Please type a few characters to kick off remote search.`
    }

    if (sourceType === "pod") {
      if (searchingForPods === true) {
        return `Searching...`
      }
      if (podOptions.length === 0) {
        return `No results found.`
      }
    } else if (sourceType === "campaign") {
      if (searchingForCampaigns === true) {
        return `Searching...`
      }
      if (campaignOptions.length === 0) {
        return `No results found.`
      }
    } else if (sourceType === "show") {
      if (searchingForShows === true) {
        return `Searching...`
      }
      if (showOptions.length === 0) {
        return `No results found.`
      }
    }

    return `Something went wrong.`
  }

  const getPodSearchResults = (searchTerm) => {
    if (searchTerm.length <= 0) {
      setSearchingForPods(false);
      setPodOptions([]);
    } else {
      setSearchingForPods(true);
      util
        .getPods({
          sort: `name`,
          page: 1,
          search: searchTerm,
          aws_status: 2,
        })
        .then((data) => {
          setSearchingForPods(false);
          setPodOptions(util.returnSimpleSelectOptions(data.results));
        });
    }
  }

  const getCampaignSearchResults = (searchTerm) => {
    if (searchTerm.length <= 0) {
      setSearchingForCampaigns(false);
      setCampaignOptions([]);
    } else {
      setSearchingForCampaigns(true);
      util.getSearchResults(searchTerm, `campaigns`)
        .then((data) => {
          setSearchingForCampaigns(false);
          setCampaignOptions(util.returnSimpleSelectOptions(data.results));
        });
    }
  }

  const getShowSearchResults = (searchTerm) => {
    if (searchTerm.length <= 0) {
      setSearchingForShows(false);
      setShowOptions([]);
    } else {
      setSearchingForShows(true);
      util
        .getShows({
          searchTerm: searchTerm,
          excludeShows: getShowsInUse().join(","),
        })
        .then((data) => {
          setSearchingForShows(false);
          setShowOptions(util.returnSimpleSelectOptionsForShows(data.results));
        });
    }
  }

  const getPodSearchResultsDelayed = useCallback(
    _.debounce(getPodSearchResults, 250),
    []
  )

  const getCampaignSearchResultsDelayed = useCallback(
    _.debounce(getCampaignSearchResults, 250),
    []
  )

  const getShowSearchResultsDelayed = useCallback(
    _.debounce(getShowSearchResults, 250),
    [shows]
  )

  const handleSelectOnValueChange = (selectedValue, action, sourceType, index) => {
    const showsArray = [...shows];
    if (action.action === "select-option") {
      if (sourceType === "pod") {
        setPod(selectedValue);
      } else if (sourceType === "campaign") {
        setCampaign(selectedValue);
      } else if (sourceType === "show") {
        showsArray[index] = {
          "label": selectedValue.label,
          "value": selectedValue.value,
          "id": selectedValue.value.toString()
        };
        setShows(showsArray);
      }
    } else if (action.action === "clear") {
      if (sourceType === "pod") {
        setPod(getDefaultPodValue());
      } else if (sourceType === "campaign") {
        setCampaign(getDefaultCampaignValue());
      } else if (sourceType === "show") {
        if (showsArray.length === 1) {
          showsArray[0] = getDefaultShowsValue()[0];
        } else {
          showsArray.splice(index, 1);
        }
        setShows(showsArray);
      }
    }
  }

  // update the ruleByWeekday value;
  const changeRuleByweekday = (e) => {
    // current array of options
    // !!! important - as you cannot update the hook directly
    // and doing 'const options = ruleByweekday;' would only copy the reference
    // so you need a new array (spread, map,...) to copy the values from ruleByweekday
    // also, this is a shallow copy (1st level, not nested), which suffice in our case
    // the other solution would be to provide a function to setRuleByweekday
    const options = [...ruleByweekday];
    const value = Number(e.target.value);

    // check if the check box is checked or unchecked
    if (e.target.checked) {
      // add the numerical value of the checkbox to options array
      options.push(value);
    } else {
      // or remove the value from the unchecked checkbox from the array
      options.splice(options.indexOf(value), 1);
    }

    // update the state with the new array of options
    setRuleByweekday(options);
  }

  // save the data;
  const onSubmit = () => {
    // build the object to send to the server;
    // can only submit a campaign or a pod, not both;
    const report = {
      id: id,
      name: name.trim(),
      sourceType: sourceType,
      pod: null,
      campaign: null,
      frequency: frequency,
      startDateValidate: startDate,
      endDateValidate: endDate,
      airtimeType: airtimeType,
      timeslots: [],
      shows: [],
      position_before_show: positionBeforeShow,
      position_after_show: positionAfterShow,
    };

    // do it this way so eslint doesn't yell at us for using underscores in var names;
    report[`end_date`] = report.endDateValidate.format(util.dateFormatComputer);
    report[`start_date`] = report.startDateValidate.format(util.dateFormatComputer);

    // depending on what the sourceType value is, record the appropriate SELECT value;
    if (report.sourceType === `campaign`) {
      if (campaign && campaign.value) {
        report.campaign = campaign.value;
      }
    } else {
      if (pod && pod.value) {
        report.pod = pod.value;
      }
    }

    // same for airtimeType
    if (report.airtimeType === `timeSlots`) {
      if (timeslots && timeslots.length > 0) {
        report.timeslots = timeslots;
      }
    } else {
      // insert only valid shows into the obj to send;
      shows && shows.forEach((show) => {
        show.value > 0 && report.shows.push(show);
      });
    }

    // determine the reoccurrence rules;
    // not all values can be sent with each request;
    switch (report.frequency.toString()) {
    case `3`:
      report.rule = {};
      break;
    case `2`:
      report.rule = {
        interval: ruleInterval,
        byweekday: ruleByweekday
      };
      break;
    case `1`:
      report.rule = {
        interval: ruleInterval,
        bymonthday: ruleBymonthday
      };
      break;
    default:
      break;
    }

    // see if there are any errors before proceeding;
    const validationResult = ruleFormValidation(report);

    // danger will robinson, we have errors;
    if (validationResult.hasErrors) {
      setErrors(validationResult.errors);
      util.putFoucsOnError();

      // stop any further processing;
      return false;

    // good to go, clear any existing errors;
    } else {
      setErrors({})
    }

    // put the button into a processing state;
    const $button = jQuery(`.btn-primary`).button(`loading`);

    // save (POST or PUT) the form data;
    util.saveRule(id, report)
      .fail((error) => {
        const serializerNameExistsError = "A Rule with this name already exists.";
        const api_error = _.get(error, "responseJSON.Validation_Error[0]")

        // if the name already exists
        if (serializerNameExistsError === api_error) {
          setErrors(_.merge({}, errors, { name: api_error}));
          util.putFoucsOnError();
        // if there is a different backend (4xx, 5xx) error
        } else {
          ajax.onFail(error);
        }

        setTimeout(() => {
          // reset the button on fail so it can be clicked again;
          $button.button(`reset`);
        }, util.delayForm);

        // stop any further processing, as the Rule was not saved in the DB
        return false;
      })
      .then((data) => {
        // after the rule has been saved, check to see if we have any conflicts;
        util.getConflicts()
          .then((conflictData) => {
            const newUrl = (conflictData.count > 0) ? util.returnConflictFormUrl() : util.returnRuleDetailUrl(data.id);

            // wait a little bit (so the user can track what’s going on);
            setTimeout(() => {
              // load the next page (rule detail or conflict form);
              browserHistory.push(newUrl);
            }, util.delayForm);
          });
      });
  }

  // cancel this action (do nothing and go somewhere else);
  const onCancel = () => {
    const newUrl = (id === 0) ? util.returnRuleListUrl() : util.returnRuleDetailUrl(id);

    browserHistory.push(newUrl);
  }

  if (loaded === false) {
    return (
      <LoadingIndicatorHeader />
    );
  } else {
    const tooltipMessagePods = "Only pods in a 'Ready' status<br />can be scheduled.";
    const tooltipMessageShows = "Show-based scheduling<br />can only apply to the next 60 days.";

    return (
      <form className="rule-form" action="#">
        <div className="row">
          <div className="col-sm-6">
            <h4 className="page__title">{ id === 0 ? `New Rule` : `Edit Rule` }</h4>
          </div>
          <div className="col-sm-6 hidden-xs">
            <RuleFormButtons
              onCancel={ () => onCancel() }
              onSubmit={ () => onSubmit() } />
          </div>
        </div>

        <hr />

        <div className={ errors.name ? `form-group has-error` : `form-group` }>
          <label className="control-label" htmlFor="title">Rule Name</label>
          <input
            type="text"
            id="title"
            className="form-control"
            value={ name }
            autoFocus
            onChange={ (e) => setName(e.target.value) }
            maxLength="100" />
          <p className={ errors.name ? `help-block` : `hidden` }>
            { errors.name }
          </p>
        </div>

        <hr />

        <h3 className="page__header">Source</h3>

        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label className="control-label">Type</label>
              <div className="radio">
                <label className="radio-inline">
                  <input
                    type="radio"
                    name="sourceType"
                    value="pod"
                    checked={ sourceType === `pod` }
                    onChange={ (e) => {
                      setSourceType(e.target.value);
                      setCampaign(null);
                    }} /> Pod
                </label>

                <label className="radio-inline">
                  <input
                    type="radio"
                    name="sourceType"
                    value="campaign"
                    checked={ sourceType === `campaign` }
                    onChange={ (e) => {
                      setSourceType(e.target.value);
                      setPod(null);
                    }} /> Campaign
                </label>
              </div>
            </div>
          </div>
          <div className="col-sm-6">
            <div id="rule-form__source--pod" className={ sourceType === `pod` ? `` : `hidden` }>
              <div className={ errors.pod ? `form-group has-error` : `form-group` }>
                <label className="control-label">
                  Source Pod&nbsp;
                  <i
                    data-tip={tooltipMessagePods}
                    data-for="ruleFormPodStatus"
                    className="glyphicon glyphicon-question-sign"
                    aria-hidden="true"
                  ></i>
                </label>
                <Tooltip id="ruleFormPodStatus" offset_top={"-6"}/>
                <Select
                  className="rule-form-select"
                  value={pod === null ? getDefaultPodValue() : pod}
                  options={podOptions}
                  placeholder="Select a pod"
                  theme="bootstrap3"
                  isClearable={true}
                  blurInputOnSelect
                  filterOption={
                    (dbOption, userInput) => {return dbOption;}
                  }
                  noOptionsMessage={
                    (searchTerm) => {
                      return (
                        <div className="no-results-found">
                          {getSelectSearchStatus(searchTerm, "pod")}
                        </div>
                      );
                    }
                  }
                  onInputChange={
                    (searchTerm) => {
                      // updating the search status on the UI
                      setSearchingForPods(true);
                      // making the backend call, with delay
                      getPodSearchResultsDelayed(searchTerm);
                    }
                  }
                  onChange={
                    (value, action) => {
                      handleSelectOnValueChange(value, action, "pod");
                    }
                  }
                />
                <p className={ errors.pod ? `help-block` : `hidden` }>Source Pod is a required field.</p>
              </div>
            </div>
            <div id="rule-form__source--campaign" className={ sourceType === `campaign` ? `` : `hidden` }>
              <div className={ errors.campaign ? `form-group has-error` : `form-group` }>
                <label className="control-label">Source Campaign</label>
                <Select
                  className="rule-form-select"
                  value={campaign === null ? getDefaultCampaignValue() : campaign}
                  options={campaignOptions}
                  placeholder="Select a campaign"
                  theme="bootstrap3"
                  isClearable={true}
                  blurInputOnSelect
                  noOptionsMessage={
                    (searchTerm) => {
                      return (
                        <div className="no-results-found">
                          {getSelectSearchStatus(searchTerm, "campaign")}
                        </div>
                      );
                    }
                  }
                  onInputChange={
                    (searchTerm) => {
                      // updating the search status on the UI
                      setSearchingForCampaigns(true);
                      // making the backend call, with delay
                      getCampaignSearchResultsDelayed(searchTerm);
                    }
                  }
                  onChange={
                    (value, action) => {
                      handleSelectOnValueChange(value, action, "campaign");
                    }
                  }
                />
                <p className={ errors.campaign ? `help-block` : `hidden` }>Source Campaign is a required field.</p>
              </div>
            </div>
          </div>
        </div>

        <hr />

        <h3 className="page__header">Schedule</h3>

        <div className="row">
          <div className="col-sm-6">
            <div className="well">
              <div>
                <label className="control-label">Repeats</label>
                <div className="radio">
                  <label className="radio-inline">
                    <input
                      type="radio"
                      name="repeats"
                      value={ 3 }
                      checked={ frequency === 3 }
                      onChange={ (e) => setFrequency(Number(e.target.value)) } />
                    Daily
                  </label>

                  <label className="radio-inline">
                    <input
                      type="radio"
                      name="repeats"
                      value={ 2 }
                      checked={ frequency === 2 }
                      disabled={ airtimeType === `showBased` }
                      onChange={ (e) => setFrequency(Number(e.target.value)) } />
                    Weekly
                  </label>

                  <label className="radio-inline">
                    <input
                      type="radio"
                      name="repeats"
                      value={ 1 }
                      checked={ frequency === 1 }
                      disabled={ airtimeType === `showBased` }
                      onChange={ (e) => setFrequency(Number(e.target.value)) } />
                    Monthly
                  </label>
                </div>
              </div>

              {
                frequency === 2 &&
                <div id="rule-form__schedule--weekly">
                  <hr />

                  <div className="form-group">
                    <div className="input-group">
                      <span className="input-group-addon">Repeat Every</span>
                      <input
                        type="number"
                        className="form-control"
                        value={ ruleInterval }
                        onChange={ (e) => setRuleInterval(Number(e.target.value)) } />
                      <span className="input-group-addon">week(s)</span>
                    </div>
                  </div>

                  <label className="control-label">Repeat On</label>
                  <div className="row">
                    <div className="col-sm-6">
                      {
                        util.days.map((day, index) => {
                          if (index < 4) {
                            return (
                              <div className="form-group" key={ day.value }>
                                <div className="checkbox">
                                  <label className="checkbox-inline">
                                    <input
                                      type="checkbox"
                                      name="repeatWeeksDays"
                                      value={ day.value }
                                      checked={ ruleByweekday.indexOf(day.value) > -1 }
                                      onChange={ (e) => changeRuleByweekday(e) } />
                                    { day.label }
                                  </label>
                                </div>
                              </div>
                            );
                          } else {
                            return null;
                          }
                        })
                      }
                    </div>

                    <div className="col-sm-6">
                      {
                        util.days.map((day, index) => {
                          if (index > 3) {
                            return (
                              <div className="form-group" key={ day.value }>
                                <div className="checkbox">
                                  <label className="checkbox-inline">
                                    <input
                                      type="checkbox"
                                      name="repeatWeeksDays"
                                      value={ day.value }
                                      checked={ ruleByweekday.indexOf(day.value) > -1 }
                                      onChange={ (e) => changeRuleByweekday(e) } />
                                    { day.label }
                                  </label>
                                </div>
                              </div>
                            );
                          } else {
                            return null;
                          }
                        })
                      }
                    </div>
                  </div>
                </div>
              }

              {
                frequency === 1 &&
                <div id="rule-form__schedule--monthly">
                  <hr />

                  <div className="form-group">
                    <div className="input-group">
                      <span className="input-group-addon">Repeat Every</span>
                      <input
                        type="number"
                        className="form-control"
                        value={ ruleInterval }
                        onChange={ (e) => setRuleInterval(Number(e.target.value)) } />
                      <span className="input-group-addon">month(s)</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="input-group">
                      <span className="input-group-addon">On Day</span>
                      <input
                        type="number"
                        className="form-control"
                        value={ ruleBymonthday }
                        onChange={ (e) => setRuleBymonthday(Number(e.target.value)) } />
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>

          <div className="col-sm-6">
            <div className="row">
              <div className="col-sm-6">
                <div className={ errors.startDate ? `form-group has-error` : `form-group` }>
                  <label className="control-label" htmlFor="start">Start Date</label>
                  <div className="input-group">
                    <div
                      className="input-group-addon"
                      onClick={ () => document.querySelector(`#start`).focus() }>
                      <i className="fa fa-calendar" aria-hidden="true"></i>
                    </div>
                    <div className="datepicker__container">
                      <DatePicker
                        id="start"
                        className="form-control"
                        popperClassName="rule-form__datepicker"
                        fixedHeight
                        selected={ startDate }
                        selectsStart
                        startDate={ startDate }
                        endDate={ endDate }
                        onChange={ (value) => setStartDate(value) } />
                    </div>
                  </div>
                  <p className={ errors.startDate ? `help-block` : `hidden` }>Start Date must be a valid date, and less than End Date.</p>
                </div>
              </div>

              <div className="col-sm-6">
                <div className={ errors.endDate || errors.showBasedEndDate ? `form-group has-error` : `form-group` }>
                  <label className="control-label" htmlFor="end">End Date</label>
                  <div className="input-group">
                    <div
                      className="input-group-addon"
                      onClick={ () => document.querySelector(`#end`).focus() }>
                      <i className="fa fa-calendar" aria-hidden="true"></i>
                    </div>
                    <div className="datepicker__container">
                      <DatePicker
                        id="end"
                        className="form-control"
                        popperClassName="rule-form__datepicker"
                        fixedHeight
                        selected={ endDate }
                        selectsEnd
                        startDate={ startDate }
                        endDate={ endDate }
                        onChange={ (value) => setEndDate(value) } />
                    </div>
                  </div>
                  <p className={ errors.endDate ? `help-block` : `hidden` }>End Date must be a valid date, and greater than Start Date.</p>
                  <p className={ errors.showBasedEndDate ? `help-block` : `hidden` }>
                    { errors.showBasedEndDate }
                  </p>
                </div>
              </div>
            </div>

            <hr />

            <h3 className="page__header">Time Slots</h3>

            <div className="row">
              <div className="col-sm-6">
                <div className="form-group">
                  <label className="control-label">Type</label>
                  <div className="radio">
                    <label className="radio-inline">
                      <input
                        type="radio"
                        name="airtimeType"
                        value="timeSlots"
                        checked={ airtimeType === `timeSlots` }
                        onChange={ (e) => {
                          setAirtimeType(e.target.value);
                          setTimeslots([]);
                          setPositionBeforeShow(false);
                          setPositionAfterShow(false);
                        }}
                      /> Select Slots
                    </label>
                    <label className="radio-inline">
                      <input
                        type="radio"
                        name="airtimeType"
                        value="showBased"
                        checked={ airtimeType === `showBased` }
                        onChange={ (e) => {
                          setAirtimeType(e.target.value);
                          setShows(getDefaultShowsValue());
                          setFrequency(3);
                        }}
                      /> Show-based
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div id="rule-form__airtime--timeslot" className={ airtimeType === `timeSlots` ? `` : `hidden` }>
                <RuleFormTimeSlots
                  error={ errors.timeslots ? true : false }
                  value={ timeslots }
                  onChange={ (timeslots) => setTimeslots(timeslots) } />
              </div>
              <div id="rule-form__airtime--show" className={ airtimeType === `showBased` ? `` : `hidden` }>
                <div className={ errors.shows ? `form-group has-error` : `form-group` }>
                  <label className="control-label">
                    Add Show{ shows.length > 1 ? `s` :  `` }&nbsp;
                    <i
                      data-tip={tooltipMessageShows}
                      data-for="ruleFormShowInfo"
                      className="glyphicon glyphicon-question-sign"
                      aria-hidden="true"
                    ></i>
                  </label>
                  <Tooltip id="ruleFormShowInfo" offset_top={"-6"}/>
                  <div className="well">
                    {
                      shows.map((show, index) => (
                        <div className="form-group" key={index}>
                          <Select
                            className="rule-form-select"
                            value={show}
                            options={showOptions}
                            placeholder="Select a show"
                            theme="bootstrap3"
                            isClearable={true}
                            blurInputOnSelect
                            noOptionsMessage={
                              (searchTerm) => {
                                return (
                                  <div className="no-results-found">
                                    {getSelectSearchStatus(searchTerm, "show")}
                                  </div>
                                );
                              }
                            }
                            onInputChange={
                              (searchTerm) => {
                                // updating the search status on the UI
                                setSearchingForShows(true);
                                // making the backend call, with delay
                                getShowSearchResultsDelayed(searchTerm);
                              }
                            }
                            onChange={
                              (value, action) => {
                                handleSelectOnValueChange(value, action, "show", index);
                              }
                            }
                          />
                        </div>
                      ))
                    }
                  </div>
                  <p className={ errors.shows ? `help-block` : `hidden` }>Show is a required field.</p>
                  <button
                    type="button"
                    id="rule-form__add--show"
                    className="btn btn-default"
                    onClick={ () => addShow() }>
                    <i className="fa fa-plus" aria-hidden="true"></i>
                    Add Another Show
                  </button>
                </div>
                <div className={ errors.position ? `form-group has-error` : `form-group` }>
                  <h6>&nbsp;</h6>
                  <label className="control-label">Select position</label>
                  <div className="form-group checkbox">
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={ positionBeforeShow === true }
                        onChange={ (e) => {
                          e.target.checked ? setPositionBeforeShow(true) : setPositionBeforeShow(false)
                        }} />
                      Before Show{ shows.length > 1 ? `s` :  `` }&nbsp;
                    </label>
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={ positionAfterShow === true }
                        onChange={ (e) => {
                          e.target.checked ? setPositionAfterShow(true) : setPositionAfterShow(false)
                        }} />
                      After Show{ shows.length > 1 ? `s` :  `` }&nbsp;
                    </label>
                  </div>
                  <p className={ errors.position ? `help-block` : `hidden` }>Select position is a required field.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr />

        <RuleFormButtons
          onCancel={ () => onCancel() }
          onSubmit={ () => onSubmit() } />
      </form>
    );
  }
}

RuleForm.propTypes = {
  params: PropTypes.object
};
